import React, { useState, useEffect, useRef, useCallback } from 'react';
import useStackTables from '../../hooks/useStackTables';
import { useNavigate, useLocation } from 'react-router-dom';
import KeepAliveOutlet from './KeepAliveOutlet';
import useSwipeToClose from '../../hooks/useSwipeToClose';
import usePullToRefresh from '../../hooks/usePullToRefresh';
import { useAuth } from '../../context/AuthContext';
import { MoreHorizontal, X, Search, Command, RefreshCw } from 'lucide-react';
import NotificationBell from './NotificationBell';
import ProfileMenu from './ProfileMenu';
import ViewModeToggle from './ViewModeToggle';
import SearchSuggestions from './SearchSuggestions';
import { navItems, flatNavItems, getPageTitle } from './navConfig';
import { featureOff, useFeatureFlags } from '../../utils/featureFlags';
import AppMoreSheet from './AppMoreSheet';
import CommandPalette from './CommandPalette';
import { buildMoreSections, flattenLeaves, leafForPath, loadPins, savePins, loadRecent, rememberRecent, togglePin } from '../../utils/appMenu';
import './appView.css';
import { customerAPI, settingsAPI, gtplSaathiAPI } from '../../services/api';
import toast from 'react-hot-toast';
import useAppearance from '../../theme/useAppearance';
import { HIDE_GTPL } from '../../utils/demo';
import { brandFor } from '../../utils/appBrand';
import { useCompanyIdentity } from '../../hooks/useCompanyIdentity';
import SubscriptionBanner from './SubscriptionBanner';

// Fallback when no admin-configured App View settings have been saved yet
// (Settings → App View → Bottom Tabs & Menu). Pulled from flatNavItems (not
// hand-rolled) so the default "Customers" tab automatically carries the same
// isGroupPicker/children data as a custom-picked one — tapping it always asks
// CableTV vs Broadband instead of guessing and opening an unfiltered list.
const DEFAULT_TAB_IDS = ['/dashboard', 'customers', '/payments', '/renewals'];
const DEFAULT_TABS = DEFAULT_TAB_IDS.map(id => flatNavItems.find(i => i.id === id)).filter(Boolean);

const isTabActive = (pathname, to) => pathname === to || pathname.startsWith(to + '/');

export default function AdminAppLayout({ viewMode, onToggleViewMode }) {
  const brand = brandFor(useCompanyIdentity('catv'));
  const { user, logout, isAdmin, hasModuleRight } = useAuth();
  useAppearance();
  useSwipeToClose();
  const [refreshNonce, setRefreshNonce] = useState(0);
  const mainRef = useRef(null);
  const ptrRef = useRef(null);
  usePullToRefresh(mainRef, ptrRef, useCallback(() => setRefreshNonce(n => n + 1), []));
  // GTPL section's own on/off preference (Settings → GTPL Saathi) — only fetched for
  // users who could possibly see the section at all.
  const flags = useFeatureFlags(); // pages of features switched off for this operator are left out of the menu
  const canSeeGtpl = isAdmin || hasModuleRight('gtplSaathi');
  const [gtplShowInNav, setGtplShowInNav] = useState(!HIDE_GTPL); // demo and operator hostnames never show the GTPL section
  useEffect(() => {
    if (!canSeeGtpl) return;
    gtplSaathiAPI.getSessionStatus()
      .then(({ data }) => setGtplShowInNav(!HIDE_GTPL && data.data?.showInNav !== false))
      .catch(() => {});
  }, [canSeeGtpl]);
  const canSeeNavItem = (item) => {
    // flatNavItems group entries key off `id`, raw navItems entries off `key`, and a
    // directly-picked child (App View custom tabs) carries `groupKey` instead.
    if ((item.id === 'gtpl' || item.key === 'gtpl' || item.groupKey === 'gtpl') && !gtplShowInNav) return false;
    if (featureOff(item.to || item.groupTo, flags)) return false;
    return !item.adminOnly || isAdmin || (item.moduleKey && hasModuleRight(item.moduleKey));
  };
  // Group children may carry their own gate (Complaints/Requests, GTPL's admin-only
  // Session & Settings) — hide the ones this user has no right to, mirroring AdminLayout's sidebar.
  const visibleChildren = (item) => (item.children || [])
    .filter(c => (!c.moduleKey || isAdmin || hasModuleRight(c.moduleKey)) && (!c.adminOnly || isAdmin) && !featureOff(c.to, flags));
  const navigate = useNavigate();
  const location = useLocation();
  const shellRef = useRef(null);
  useStackTables(shellRef);
  const [moreOpen, setMoreOpen] = useState(false);
  const [pins, setPins] = useState(loadPins);
  const [recent, setRecent] = useState(loadRecent);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [tabs, setTabs] = useState(DEFAULT_TABS);
  const [moreOrder, setMoreOrder] = useState(null); // null = no custom order saved yet
  const [moreHidden, setMoreHidden] = useState([]);
  // Holds the group entry whose picker is open (null = closed). Used to be a bare boolean
  // that always looked up the FIRST group in flatNavItems — so putting any group other than
  // Customers on the bottom bar opened the Customers picker.
  const [groupPicker, setGroupPicker] = useState(null);

  useEffect(() => {
    settingsAPI.getAppView()
      .then(r => {
        const d = r.data.data || {};
        if (d.bottomTabs?.length) {
          const picked = d.bottomTabs
            .map(id => flatNavItems.find(i => i.id === id))
            .filter(Boolean)
            .filter(canSeeNavItem);
          if (picked.length) setTabs(picked);
        }
        if (d.moreOrder?.length) setMoreOrder(d.moreOrder);
        if (d.moreHidden?.length) setMoreHidden(d.moreHidden);
      })
      .catch(() => {});
  }, [isAdmin]);

  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  // Everything this user may open — feeds the More sheet's search, pins and recent row.
  const leaves = flattenLeaves({ navItems, canSee: canSeeNavItem, visibleChildren });
  const sections = buildMoreSections({ navItems, canSee: canSeeNavItem, visibleChildren, moreOrder, moreHidden });
  useEffect(() => {
    const leaf = leafForPath(leaves, location.pathname);
    if (leaf) setRecent(rememberRecent(leaf.id));
  }, [location.pathname]); // eslint-disable-line
  const handleTogglePin = (id) => setPins((p) => { const next = togglePin(p, id); savePins(next); return next; });

  const isImpersonating = !!localStorage.getItem('adminToken');
  const impersonatingName = localStorage.getItem('impersonatingName');

  const handleSwitchBack = () => {
    const adminToken = localStorage.getItem('adminToken');
    const adminRefresh = localStorage.getItem('adminRefreshToken');
    localStorage.setItem('token', adminToken);
    if (adminRefresh) localStorage.setItem('refreshToken', adminRefresh);
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminRefreshToken');
    localStorage.removeItem('impersonatingName');
    window.location.href = '/agent-collection';
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    try {
      const results = await customerAPI.search(q, 1);
      const match = results[0];
      if (!match) { toast.error('No customer found'); return; }
      navigate(`/customers/${match._id}`);
      setSearchQuery('');
      setSuggestOpen(false);
      setSearchOpen(false);
    } catch {
      toast.error('Search failed');
    }
  };

  const handlePickSuggestion = (customer) => {
    navigate(`/customers/${customer._id}`);
    setSearchQuery('');
    setSuggestOpen(false);
    setSearchOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="app-view" ref={shellRef} style={{ height: '100vh', maxHeight: '100dvh', overflow: 'hidden', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <header style={{
        position: 'sticky', top: 0, zIndex: 60, background: 'var(--bg-card, #fff)',
        borderBottom: '1px solid var(--border)', flexShrink: 0,
        // Safari standalone/app view draws under the status bar — safe-area padding keeps
        // the title and buttons reachable below the notch (viewport-fit=cover in index.html).
        height: 'calc(56px + env(safe-area-inset-top, 0px))',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        paddingLeft: 10, paddingRight: 10, gap: 8,
      }}>
        <img src={brand.logo} alt={brand.name} style={{ height: 42, objectFit: 'contain', flexShrink: 0 }} />
        <span style={{
          fontWeight: 700, fontSize: '.92rem', flex: 1, textAlign: 'center',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {getPageTitle(location.pathname)}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          <button
            type="button"
            aria-label={searchOpen ? 'Close search' : 'Search'}
            onClick={() => setSearchOpen(o => !o)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 6, display: 'flex' }}
          >
            {searchOpen ? <X size={18} /> : <Search size={18} />}
          </button>
          <button
            type="button"
            aria-label="Jump to any page"
            onClick={() => window.dispatchEvent(new Event('open-command-palette'))}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 6, display: 'flex' }}
          >
            <Command size={18} />
          </button>
          <ViewModeToggle viewMode={viewMode} onToggle={onToggleViewMode} />
          <NotificationBell />
          <ProfileMenu user={user} onLogout={handleLogout} size={28} />
        </div>
      </header>

      {isImpersonating && (
        <div style={{
          background: 'var(--cb-f59e0b)', color: 'var(--c-1c1917)', padding: '6px 12px', display: 'flex',
          alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6,
          fontSize: '.75rem', fontWeight: 700,
        }}>
          <span>👤 Viewing as agent: {impersonatingName}</span>
          <button onClick={handleSwitchBack} style={{ background: 'var(--cb-1c1917)', color: 'var(--c-ffffff)', border: 'none', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontSize: '.72rem', fontWeight: 700 }}>
            Switch Back
          </button>
        </div>
      )}

      {searchOpen && (
        <form onSubmit={handleSearch} style={{
          position: 'relative', padding: '8px 12px', background: 'var(--bg-card, #fff)',
          borderBottom: '1px solid var(--border)',
        }}>
          <Search size={15} style={{ position: 'absolute', left: 24, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
          <input
            className="search-input"
            style={{ width: '100%', paddingLeft: 34, height: 38, borderRadius: 8 }}
            placeholder="Search customer (name, mobile, account, STB)..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onFocus={() => setSuggestOpen(true)}
            onBlur={() => setSuggestOpen(false)}
            autoFocus
          />
          {suggestOpen && <SearchSuggestions query={searchQuery} onPick={handlePickSuggestion} />}
        </form>
      )}

      <main ref={mainRef} style={{ position: 'relative', flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}>
        <div ref={ptrRef} className="av-ptr" aria-hidden="true"><RefreshCw size={18} /></div>
        <div className="page-content" style={{ padding: 14 }}>
          {isAdmin && <SubscriptionBanner />}
          <KeepAliveOutlet tabPaths={tabs.map(t => t.to)} nonce={refreshNonce} />
          <CommandPalette leaves={leaves} />
        </div>
      </main>

      {/* Bottom tab bar */}
      <nav className="av-tabs" style={{
        position: 'sticky', bottom: 0, background: 'var(--bg-card, #fff)', borderTop: '1px solid var(--border)',
        display: 'flex', flexShrink: 0, zIndex: 60,
        // Same safe-area treatment at the bottom (iPhone home indicator).
        height: 'calc(58px + env(safe-area-inset-bottom, 0px))',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}>
        {/* Sliding indicator: tabs are equal-width, so its left edge is just index × width. */}
        <span className="av-tab-ind" aria-hidden="true" style={{
          width: `${100 / (tabs.length + 1)}%`,
          transform: `translateX(${(moreOpen ? tabs.length : tabs.findIndex(t => isTabActive(location.pathname, t.to))) * 100}%)`,
          opacity: moreOpen || tabs.some(t => isTabActive(location.pathname, t.to)) ? 1 : 0,
        }} />
        {tabs.map(tab => {
          const active = isTabActive(location.pathname, tab.to);
          return (
            <button
              key={tab.id || tab.to}
              className={active ? 'av-tab av-tab-on' : 'av-tab'}
              // Customers opens the list directly: the list has its own All / CableTV /
              // Broadband switch now, so the picker would only add a tap.
              onClick={() => (tab.isGroupPicker && tab.id !== 'customers' ? setGroupPicker(tab) : navigate(tab.to))}
              style={{
                flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: 2, background: 'none', border: 'none', cursor: 'pointer',
                color: active ? 'var(--primary)' : 'var(--text-muted)', fontSize: '.66rem', fontWeight: 600,
              }}
            >
              <tab.icon size={19} />
              {tab.label}
            </button>
          );
        })}
        <button
          className={moreOpen ? 'av-tab av-tab-on' : 'av-tab'}
          onClick={() => setMoreOpen(true)}
          style={{
            flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 2, background: 'none', border: 'none', cursor: 'pointer',
            color: moreOpen ? 'var(--primary)' : 'var(--text-muted)', fontSize: '.66rem', fontWeight: 600,
          }}
        >
          <MoreHorizontal size={19} />
          More
        </button>
      </nav>

      {/* "More" sheet — search, pinned/recent, grouped tiles (AppMoreSheet) */}
      {moreOpen && (
        <AppMoreSheet
          sections={sections} leaves={leaves} pins={pins} recent={recent}
          onTogglePin={handleTogglePin}
          onOpen={(entry) => { navigate(entry.to); setMoreOpen(false); }}
          onClose={() => setMoreOpen(false)}
          onLogout={handleLogout}
        />
      )}

      {/* A grouped bottom tab (Customers, Complaints/Requests) is a picker, not a direct
          link — it asks which child to open instead of guessing. */}
      {groupPicker && (() => {
        // Children the user can't see are dropped, same rule as the More menu.
        const children = (groupPicker.children || [])
          .filter(c => (!c.moduleKey || isAdmin || hasModuleRight(c.moduleKey)) && (!c.adminOnly || isAdmin));
        return (
          <div onClick={() => setGroupPicker(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 110, display: 'flex', alignItems: 'flex-end' }}>
            <div
              data-sheet
              onClick={e => e.stopPropagation()}
              style={{
                width: '100%', background: 'var(--bg-card, #fff)', borderRadius: '16px 16px 0 0',
                padding: '16px 16px 24px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <span style={{ fontWeight: 700, fontSize: '.95rem' }}>{groupPicker.label}</span>
                <button data-sheet-close onClick={() => setGroupPicker(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                  <X size={20} />
                </button>
              </div>
              {/* Wraps rather than squeezing — this picker is no longer always exactly two items. */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {children.map(child => (
                  <button
                    key={child.to}
                    onClick={() => { navigate(child.to); setGroupPicker(null); }}
                    style={{
                      flex: '1 1 30%', minWidth: 96, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                      padding: '18px 10px', border: '1px solid var(--border)', borderRadius: 10,
                      background: 'var(--bg)', cursor: 'pointer', fontSize: '.85rem', fontWeight: 600, color: 'var(--text)',
                      textAlign: 'center',
                    }}
                  >
                    <child.icon size={22} />
                    {child.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

    </div>
  );
}
